import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

import { CommonConstants, DSComponents, DesignSystemHelperService } from '@frontend/promo-utils';
import { DsButton } from '@frontend/ui/button';
import { ContentLink } from '@frontend/vanilla/core';
import { OfferButtonBehaviorDirective } from '@frontend/vanilla/features/offer-button';
import { LetDirective } from '@ngrx/component';
import { BehaviorSubject } from 'rxjs';

import { Offer, OfferTeaserClientContent } from '../../../offers/models';
import { OfferContentConfiguration } from '../../../offers/offer-content.client-config';
import { LoadOffersApiService } from '../../../offers/offers-service/load-offers.service';
import { OffersApiService } from '../../../offers/offers-service/offers.service';
import { RoutingHistory } from '../../../route-history/route-history.service';
import { OfferTeaserService } from '../offer-teaser.service';

@Component({
    selector: 'offer-teaser-special-offer-cta',
    templateUrl: 'offer-teaser-special-offer-cta.component.html',
    styleUrl: 'offer-teaser-special-offer-cta.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [LetDirective, NgClass, OfferButtonBehaviorDirective, DsButton],
})
export class OfferTeaserSpecialOfferCtaComponent implements OnChanges {
    @Input() offer: Offer;
    @Input() teaserNumber: number;
    @Input() product: string;
    @Input() isMyGala: boolean;
    @Input() moreInfoCta: ContentLink;
    @Input() isGroupedPage: boolean = false;
    @Input() offerType: string = '';

    vm$: BehaviorSubject<OfferTeaserClientContent>;

    isSpecialOfferOptIn: boolean;
    isMoreInfoClicked = false;
    isClickEventTracked = false;
    dsButtonMoreInfoSettings: any;
    dsButtonPrimaryCtaSettings: any;
    dsButtonConfig: DSComponents;
    dsPrimaryButtonConfig: DSComponents;

    constructor(
        private offerTeaserService: OfferTeaserService,
        public loadOffersApiService: LoadOffersApiService,
        public offerContentConfiguration: OfferContentConfiguration,
        private designSystemHelperService: DesignSystemHelperService,
        private offersApiService: OffersApiService,
        private routerHistory: RoutingHistory,
    ) {
        this.vm$ = this.loadOffersApiService.offerTeaserClientContent$;
    }

    get promoId() {
        return this.offer?.offerMetadata?.crmServiceId;
    }
    get Special_offer_Id() {
        return this.offer?.offerContent?.primaryCta?.attributes != null ? this.offer?.offerContent?.primaryCta?.attributes['data-offer-id'] : '';
    }
    get Special_offer_Type() {
        return this.offer?.offerContent?.primaryCta?.attributes != null ? this.offer?.offerContent?.primaryCta?.attributes['data-offer-type'] : '';
    }

    get data_tracking_keys() {
        return JSON.stringify([
            'campaignId',
            'component.CategoryEvent',
            'component.EventDetails',
            'component.LabelEvent',
            'component.LocationEvent',
            'component.PositionEvent',
            'component.URLClicked',
            'component.contentPosition',
            'component.Product',
            'component.PromoOfferName',
            'component.PromoBonusCode',
            'page.siteSection',
            'page.siteSubSection',
            'component.PromotionType',
            'component.PromoIntent',
        ]);
    }

    get data_tracking_Values() {
        return JSON.stringify([
            this.promoId ?? 'not applicable',
            'promo hub',
            'opt in cta',
            this.product,
            this.offer?.offerMetadata?.serviceSubType ?? this.offer?.offerMetadata?.serviceType,
            this.teaserNumber,
            'not applicable',
            this.teaserNumber,
            this.offer?.offerMetadata?.applicableProducts?.toString() ?? 'not applicable',
            this.offer?.offerContent?.title,
            'not applicable',
            'promo hub',
            'promo offers page',
            'Static Promotion',
            this.offerContentConfiguration.isBetMgmTileRedesignEnabled ? 'retention' : 'not applicable',
        ]);
    }

    ngOnChanges() {
        this.dsButtonConfig = this.designSystemHelperService.getDsComponentsList(CommonConstants.Default);
        this.dsPrimaryButtonConfig = this.designSystemHelperService.getDsComponentsList(CommonConstants.MoreInfo);
        this.isSpecialOfferOptIn = this.offer?.offerMetadata?.isSpecialOfferOptin;
        this.getButtonClasses();
    }

    onPrimaryCtaClick(cta: ContentLink) {
        this.trackClickEvent(cta);
        if (cta?.url) {
            this.routerHistory.setPreviousUrl();
            this.offersApiService.goToDetailPage(cta.url);
        }
    }

    trackClickEvent(cta: ContentLink) {
        if (!this.isClickEventTracked) {
            this.offerTeaserService.trackClickEvent(
                this.teaserNumber,
                cta,
                this.product,
                this.offer?.offerMetadata?.isGroupedTile,
                this.offer?.offerMetadata?.serviceSubType,
                this.promoId,
                this.isMyGala,
                this.isGroupedPage,
                this.offerType,
                this.offer,
            );
            this.isClickEventTracked = true;
        }
    }

    navigateToOfferMoreInfo() {
        if (!this.isMoreInfoClicked) {
            this.offerTeaserService.navigateToOfferMoreInfo(this.teaserNumber, this.moreInfoCta, this.offer, false, this.product);
            this.isMoreInfoClicked = true;
        }
    }

    specialOffer_Click(evt: any) {
        if (evt?.currentTarget?.attributes['data-offer-status']?.value !== 'offered') {
            this.navigateToOfferMoreInfo();
        }
    }
    getButtonClasses() {
        this.moreInfoCtaClass();
        if (this.isSpecialOfferOptIn) {
            if (this.offer.offerContent.primaryCta && this.offer.offerContent.primaryCta.attributes) {
                this.dsButtonPrimaryCtaSettings = {
                    size: this.offer.offerContent.primaryCta.attributes['data-btn-size'] ?? this.dsPrimaryButtonConfig?.dsButton?.size,
                    variant: this.offer.offerContent.primaryCta.attributes['data-btn-variant'] ?? this.dsPrimaryButtonConfig?.dsButton?.variant,
                    kind: this.offer.offerContent.primaryCta.attributes['data-btn-kind'] ?? this.dsPrimaryButtonConfig?.dsButton?.kind,
                };
            }
        } else if (this.offer.offerContent.primaryCta && this.offer.offerContent.primaryCta.attributes) {
            this.dsButtonPrimaryCtaSettings = {
                size: this.offer.offerContent.primaryCta.attributes['data-btn-size'] ?? this.dsButtonConfig?.dsButton?.size,
                variant: this.offer.offerContent.primaryCta.attributes['data-btn-variant'] ?? this.dsButtonConfig?.dsButton?.variant,
                kind: this.offer.offerContent.primaryCta.attributes['data-btn-kind'] ?? this.dsButtonConfig?.dsButton?.kind,
            };
        }
    }
    moreInfoCtaClass() {
        if (this.moreInfoCta && this.moreInfoCta.attributes) {
            const size = this.offer?.offerContent?.primaryCta ? this.dsPrimaryButtonConfig?.dsButton?.size : this.dsButtonConfig?.dsButton?.size;
            const variant = this.offer?.offerContent?.primaryCta
                ? this.dsPrimaryButtonConfig?.dsButton?.variant
                : this.dsButtonConfig?.dsButton?.variant;
            const kind = this.offer?.offerContent?.primaryCta ? this.dsPrimaryButtonConfig?.dsButton?.kind : this.dsButtonConfig?.dsButton?.kind;
            this.dsButtonMoreInfoSettings = {
                size: this.moreInfoCta.attributes['data-btn-size'] ?? size,
                variant: this.moreInfoCta.attributes['data-btn-variant'] ?? variant,
                kind: this.moreInfoCta.attributes['data-btn-kind'] ?? kind,
            };
        }
    }
}
